// How TriSof runs tickets on every project (docs/ticketing.md). The statuses
// are Linear workflow states, matched by name; the board, the doc and the
// robot all read this one table so they cannot disagree on what a status is.

export const TICKET_STATUSES = [
  { key: 'triage', name: 'Triage', flow: 'main', meaning: 'New; the lead decides where it goes' },
  { key: 'backlog', name: 'Backlog', flow: 'main', meaning: 'Accepted, not planned for this cycle' },
  { key: 'todo', name: 'Todo', flow: 'main', meaning: "Planned for this week's cycle" },
  { key: 'in_progress', name: 'In Progress', flow: 'main', meaning: 'Someone is working on it' },
  // Ours only: still In Progress in Linear, but the agent is stuck on a person
  // (a question, a failed run, a usage limit). Never a Linear state.
  { key: 'needs_attention', name: 'Needs attention', flow: 'main', meaning: 'In progress, but waiting on a person', dashboardOnly: true },
  { key: 'needs_merge', name: 'Needs merge', flow: 'main', meaning: 'Code done, PR waiting to merge' },
  { key: 'ready_to_test', name: 'Ready to test', flow: 'main', meaning: 'Merged and on UAT, waiting to be tested' },
  { key: 'done', name: 'Done', flow: 'main', meaning: 'Tested on UAT and passed' },
  { key: 'released', name: 'Released', flow: 'main', meaning: 'Live in prod' },
  { key: 'blocked', name: 'Blocked', flow: 'side', meaning: 'Waiting on a third party, an approval or a key' },
  { key: 'client_response', name: 'Client response needed', flow: 'side', meaning: 'Waiting on the client' },
  { key: 'out_of_scope', name: 'Out of scope', flow: 'side', meaning: 'Not in the scope docs' },
  { key: 'duplicate', name: 'Duplicate', flow: 'side', meaning: 'Duplicate or cancelled' },
] as const

export type TicketStatus = (typeof TICKET_STATUSES)[number]['key']

/** Other names a team may already use for the same status. Compared trimmed and case-insensitive, as Linear does. */
const ALIASES: Record<string, TicketStatus> = {
  'in progress': 'in_progress', 'in review': 'needs_merge', 'needs review': 'needs_merge',
  'ready for test': 'ready_to_test', 'ready for testing': 'ready_to_test', 'uat': 'ready_to_test',
  'to do': 'todo', 'client response': 'client_response', 'waiting on client': 'client_response',
  'canceled': 'duplicate', 'cancelled': 'duplicate', 'duplicate/cancelled': 'duplicate',
}

/**
 * Which status a Linear state is, by name first and Linear's state type as a
 * fallback, so a team that has not added every status yet still lands each
 * ticket in a sensible column.
 */
export function statusOf(stateName: string | null | undefined, stateType?: string | null): TicketStatus {
  const n = (stateName ?? '').trim().toLowerCase()
  const exact = TICKET_STATUSES.find((s) => s.name.toLowerCase() === n)
  if (exact) return exact.key
  if (ALIASES[n]) return ALIASES[n]
  switch (stateType) {
    case 'triage': return 'triage'
    case 'backlog': return 'backlog'
    case 'unstarted': return 'todo'
    case 'started': return 'in_progress'
    case 'completed': return 'done'
    case 'canceled': return 'duplicate'
    default: return 'triage'
  }
}

/** Statuses the team's Linear states do not have yet. Nothing is created: a lead adds them in Linear. */
export function missingStatuses(stateNames: readonly string[]): string[] {
  const have = new Set(stateNames.map((n) => n.trim().toLowerCase()))
  return TICKET_STATUSES.filter((s) => !('dashboardOnly' in s) && !have.has(s.name.toLowerCase())).map((s) => s.name)
}

/** Linear's exponential estimate scale. Out-of-scope tickets never get one. */
export const ESTIMATES = [
  { points: 1, time: 'Under 1 hour', what: 'Wording, label or spacing fix', example: 'TC-2700 "tablet" → "device"' },
  { points: 2, time: '1–3 hours', what: 'One screen with a little logic', example: 'TC-3229 Month tab on the diary' },
  { points: 4, time: 'Half to one day', what: 'Feature or fix touching data or rules', example: 'TC-3225 add contact from Booked By' },
  { points: 8, time: '2–3 days', what: 'Several screens, web and app, or a database change', example: 'TC-2436 dictionary matrix' },
  { points: 16, time: 'About a week', what: 'A new feature area or a page rebuild', example: 'TC-3175 Check-Out from Check-In' },
] as const

export type EstimatePoints = (typeof ESTIMATES)[number]['points']

/** The scale entry for a Linear estimate; an off-scale number rounds up to the next point. */
export function estimateOf(points: number | null | undefined) {
  if (points == null || points <= 0) return null
  return ESTIMATES.find((e) => e.points >= points) ?? ESTIMATES[ESTIMATES.length - 1]
}

/** Goodwill is never set by us: it is the client's call, recorded when they make it. */
export const SCOPES = ['In scope', 'Out of scope', 'Goodwill'] as const
export type Scope = (typeof SCOPES)[number]

export const SOURCES = ['Client audit', 'In-app feedback', 'ThinkTime', 'Internal', 'TriSof agent', 'Manual'] as const
export type Source = (typeof SOURCES)[number]

/** A label like "Scope: Out of scope" or "Source: ThinkTime" → its value, if it is one of ours. */
export function labelValue<T extends string>(labels: readonly string[], prefix: string, values: readonly T[]): T | null {
  for (const l of labels) {
    const m = l.match(/^\s*([^:]+):\s*(.+?)\s*$/)
    const key = m?.[1]?.trim().toLowerCase(), val = m?.[2] ?? ''
    if (key !== prefix.toLowerCase()) continue
    const v = values.find((x) => x.toLowerCase() === val.toLowerCase())
    if (v) return v
    // The older triage labels ("Scope: In" / "Scope: Out") mean the same thing.
    if (prefix.toLowerCase() === 'scope') {
      if (/^in$/i.test(val)) return values.find((x) => x === 'In scope') ?? null
      if (/^out$/i.test(val)) return values.find((x) => x === 'Out of scope') ?? null
    }
  }
  return null
}

/**
 * Every project plans in one-week cycles (Linear cycles, Monday to Monday).
 * A developer's week is POINTS_PER_PERSON. Developers are the project's
 * leads and devs; the agent is not counted, its work takes nobody's week.
 */
export const CYCLE_WEEKS = 1
export const CYCLE_START_DAY = 1 // Monday, as Linear counts days (0 = Sunday)
export const POINTS_PER_PERSON = 50

export function cycleCapacity(people: number, pointsPerPerson: number = POINTS_PER_PERSON): number {
  return Math.max(0, people) * pointsPerPerson
}
